// Dashboard.js - Quantum Enhanced Complete Version (Full 2000+ lines)
import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import {
  Container, Grid, Card, CardContent, Typography, Box, Chip,
  LinearProgress, Avatar, IconButton, Button, Paper, alpha,
  useTheme, useMediaQuery, Badge, Tooltip, Fade, Zoom,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemIcon, ListItemText, ListItemAvatar,
  Menu, MenuItem, Divider, Alert, Snackbar,
  TextField, FormControl, InputLabel, Select,
  Checkbox, CircularProgress, SpeedDial, SpeedDialAction, SpeedDialIcon,
  Tabs, Tab, FormControlLabel, Switch, Radio, RadioGroup,
  Stepper, Step, StepLabel, StepContent
} from '@mui/material';
import {
  TrendingUp, Insights, BarChart, CreditScore, RocketLaunch,
  Science, LocalShipping, Timeline, Psychology, Lightbulb,
  CompareArrows, FileDownload, Share, Notifications,
  AccountBalance, Speed, Analytics, Star, EmojiEvents,
  Whatshot, AutoGraph, PsychologyAlt, PlayArrow, Add,
  ViewList, History, Settings, Logout, Person,
  ChevronRight, CheckCircle, Warning, Info,
  Download, Upload, CloudSync, Security,
  CalendarToday, Group, Business, Flag,
  Email, Phone, Chat, SupportAgent, ContactSupport,
  Send, Close, BugReport, Feedback, Help,
  QrCode, Biotech, PrecisionManufacturing, SmartToy,
  WorkspacePremium, MilitaryTech, Diamond,
  Psychology as PsychologyIcon, Calculate,
  Analytics as AnalyticsIcon, Dashboard as DashboardIcon,
  PictureAsPdf, TableChart, DataObject,
  CloudDownload, CloudUpload, Storage,
  FormatListBulleted, DashboardCustomize,
  IntegrationInstructions, Api, Webhook,
  DataThresholding, Schema, Polyline,
  AccountTree, Lan, DeviceHub
} from '@mui/icons-material';

import { 
  Description,
  } from '@mui/icons-material';

import axios from 'axios';


import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../contexts/AuthContext';
import { ScenarioContext } from '../contexts/ScenarioContext';
import { UIContext } from '../contexts/UIContext';
import { useCurrency } from '../hooks/useCurrency';
import { useScenario } from '../contexts/ScenarioContext';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import HolographicCard from '../components/ui/HolographicCard';
import AnimatedCounter from '../components/ui/AnimatedCounter';
import DynamicGradientText from '../components/ui/DynamicGradientText';

import ROIAnalyzerDemo from '../components/demo/ROIAnalyzerDemo';

// Enhanced Quantum Particle Background Component
const QuantumParticlesEnhanced = ({ quantumMode }) => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: -1,
      opacity: quantumMode ? 0.15 : 0.08,
      background: quantumMode ? `
        radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.4) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.4) 0%, transparent 50%),
        radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.4) 0%, transparent 50%),
        radial-gradient(circle at 60% 30%, rgba(255, 219, 88, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 30% 70%, rgba(88, 255, 219, 0.3) 0%, transparent 50%)
      ` : `
        radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.3) 0%, transparent 50%)
      `,
      transition: 'all 0.8s ease-in-out',
      animation: quantumMode ? 'quantumPulse 3s ease-in-out infinite alternate' : 'none',
      '@keyframes quantumPulse': {
        '0%': { opacity: 0.1 },
        '50%': { opacity: 0.2 },
        '100%': { opacity: 0.15 }
      }
    }}
  />
);

const Dashboard = () => {
  const { user, credits, logout, token } = useContext(AuthContext);
  const { 
    scenarios: savedScenarios, 
    loading: loadingScenarios,
    fetchScenarios,
    deleteScenario,
    duplicateScenario,
    refreshScenarios
  } = useScenario();
  const { setActiveTab, triggerCreditPulse } = useContext(UIContext);
  const { selectedCurrency, currencySymbol } = useCurrency();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  // Enhanced State Management
  const [activeView, setActiveView] = useState('quantum-overview');
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [scenarioMenuAnchor, setScenarioMenuAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [quickAnalysisDialogOpen, setQuickAnalysisDialogOpen] = useState(false);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [quantumMode, setQuantumMode] = useState(false);
  const [aiInsights, setAiInsights] = useState([]);
  const [predictiveMetrics, setPredictiveMetrics] = useState({});
  const [exportTab, setExportTab] = useState('scenarios');
  const [activeExportStep, setActiveExportStep] = useState(0);

  // Add this state for the demo dialog
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);

  
  // Add these state declarations
const [supportTickets, setSupportTickets] = useState([]);
const [contactMessages, setContactMessages] = useState([]);
const [loadingSupport, setLoadingSupport] = useState(false);
const [supportStats, setSupportStats] = useState({
  openTickets: 0,
  resolvedTickets: 0,
  responseTime: '24h',
  satisfactionRate: '96%'
});

const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
  
  // Enhanced Contact & Support State
  const [contactForm, setContactForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: '',
    priority: 'medium',
    category: 'general'
  });

  const [supportForm, setSupportForm] = useState({
    issueType: 'technical',
    subject: '',
    description: '',
    urgency: 'medium',
    attachments: []
  });

  // Enhanced Export State
  const [exportState, setExportState] = useState({
    selectedScenarios: [],
    exportType: 'all-scenarios',
    exportFormat: 'pdf',
    includeCharts: true,
    includeRawData: false,
    dataRange: 'all',
    customName: '',
    compression: 'normal',
    reportType: 'executive'
  });

  // Enhanced Dashboard Metrics with Predictive Analytics
  const dashboardMetrics = useMemo(() => {
    if (!savedScenarios || savedScenarios.length === 0) {
      return {
        totalImpact: 0,
        averageROI: 0,
        scenariosCount: 0,
        successRate: 0,
        topCategory: 'N/A',
        improvementPotential: 0,
        monthlyTrend: 'stable',
        predictiveROI: 0,
        quantumScore: 0,
        efficiencyGain: 0,
        trendVelocity: '±0%',
        aiConfidence: 0,
        scenarioHealth: 0
      };
    }

    const totalImpact = savedScenarios.reduce((sum, scenario) => sum + (scenario.totalImpact || 0), 0);
    const averageROI = savedScenarios.reduce((sum, scenario) => {
      const investment = (scenario.turnover || 100000000) * 0.05;
      return sum + (investment > 0 ? (scenario.totalImpact || 0) / investment : 0);
    }, 0) / savedScenarios.length;

    const successfulScenarios = savedScenarios.filter(scenario => 
      (scenario.totalImpact || 0) > 1000000
    ).length;

    // Advanced Trend Analysis
    const currentMonthImpact = savedScenarios
      .filter(s => new Date(s.createdAt).getMonth() === new Date().getMonth())
      .reduce((sum, s) => sum + (s.totalImpact || 0), 0);
    
    const lastMonthImpact = savedScenarios
      .filter(s => new Date(s.createdAt).getMonth() === new Date().getMonth() - 1)
      .reduce((sum, s) => sum + (s.totalImpact || 0), 0);

    const monthlyTrend = currentMonthImpact > lastMonthImpact ? 'up' : 
                       currentMonthImpact < lastMonthImpact ? 'down' : 'stable';

    // Predictive Analytics with AI Confidence
    const predictiveROI = averageROI * 1.15;
    const quantumScore = Math.min(100, (successfulScenarios / savedScenarios.length) * 100 + 
      (averageROI > 3 ? 20 : 0) + (totalImpact > 5000000 ? 30 : 0));
    const efficiencyGain = Math.min(100, (savedScenarios.length * 5) + (averageROI * 10));
    const aiConfidence = Math.min(95, quantumScore * 0.8 + 20);
    const scenarioHealth = Math.min(100, (successfulScenarios / savedScenarios.length) * 100 + 
      (savedScenarios.length > 10 ? 20 : 0));

    return {
      totalImpact,
      averageROI: averageROI || 0,
      scenariosCount: savedScenarios.length,
      successRate: (successfulScenarios / savedScenarios.length) * 100,
      topCategory: 'Quantum Optimization',
      improvementPotential: totalImpact * 0.25,
      monthlyTrend,
      predictiveROI,
      quantumScore,
      efficiencyGain,
      trendVelocity: monthlyTrend === 'up' ? '+12.5%' : monthlyTrend === 'down' ? '-8.3%' : '±0%',
      aiConfidence,
      scenarioHealth
    };
  }, [savedScenarios]);


  // Enhanced Notifications State
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'success',
      title: 'Quantum Analysis Complete',
      message: 'Your ROI analysis for "Supply Chain Optimization" is ready',
      time: '5 min ago',
      read: false,
      action: () => handleViewScenario('supply-chain-opt'),
      quantum: true
    },
    {
      id: 2,
      type: 'warning',
      title: 'Quantum Credits Low',
      message: 'You have only 2 quantum credits remaining',
      time: '1 hour ago',
      read: false,
      action: () => navigate('/scenarios?tab=credits'),
      quantum: true
    },
    {
      id: 3,
      type: 'info',
      title: 'New Quantum Feature Available',
      message: 'Try the new AI-powered quantum scenario comparison',
      time: '2 hours ago',
      read: true,
      action: () => navigate('/scenarios?tab=scenarios'),
      quantum: true
    },
    {
      id: 4,
      type: 'info',
      title: 'Quantum Score Updated',
      message: `Your quantum score increased to ${dashboardMetrics.quantumScore}`,
      time: '1 day ago',
      read: true,
      action: () => setQuantumMode(true),
      quantum: true
    }
  ]);

  // Enhanced Quick Analysis Templates with Quantum Features
  const quantumTemplates = [
    {
      id: 1,
      name: 'Quantum Supply Chain',
      industry: 'All',
      description: 'AI-optimized supply chain with predictive analytics and quantum algorithms',
      estimatedTime: '3 min',
      impact: 'Very High',
      icon: <LocalShipping />,
      credits: 2,
      features: ['Predictive Analytics', 'AI Optimization', 'Real-time Tracking', 'Quantum Algorithms'],
      quantum: true,
      category: 'operations',
      successRate: 94
    },
    {
      id: 2,
      name: 'Digital Transformation AI',
      industry: 'All', 
      description: 'Quantum-powered digital transformation analysis with risk assessment',
      estimatedTime: '5 min',
      impact: 'High',
      icon: <Science />,
      credits: 3,
      features: ['Quantum AI', 'ROI Prediction', 'Risk Assessment', 'Market Analysis'],
      quantum: true,
      category: 'strategy',
      successRate: 89
    },
    {
      id: 3,
      name: 'Cost Quantum Optimization',
      industry: 'Manufacturing',
      description: 'AI-driven cost reduction with quantum algorithms and efficiency analysis',
      estimatedTime: '4 min',
      impact: 'High',
      icon: <AccountBalance />,
      credits: 2,
      features: ['Quantum Algorithms', 'Cost Prediction', 'Efficiency Analysis', 'Process Optimization'],
      quantum: true,
      category: 'finance',
      successRate: 91
    },
    {
      id: 4,
      name: 'Quantum Market Expansion',
      industry: 'All',
      description: 'Predictive market analysis with quantum computing and simulation',
      estimatedTime: '6 min',
      impact: 'Very High',
      icon: <TrendingUp />,
      credits: 4,
      features: ['Market Prediction', 'Quantum Simulation', 'Risk Analysis', 'Growth Projection'],
      quantum: true,
      category: 'marketing',
      successRate: 87
    },
    {
      id: 5,
      name: 'Operational Efficiency',
      industry: 'All',
      description: 'Standard operational efficiency analysis',
      estimatedTime: '4 min',
      impact: 'Medium',
      icon: <Speed />,
      credits: 1,
      features: ['Process Analysis', 'Efficiency Metrics', 'Improvement Tips'],
      quantum: false,
      category: 'operations',
      successRate: 85
    },
    {
      id: 6,
      name: 'Financial Health Check',
      industry: 'All',
      description: 'Comprehensive financial analysis and recommendations',
      estimatedTime: '5 min',
      impact: 'Medium',
      icon: <AccountBalance />,
      credits: 1,
      features: ['Financial Metrics', 'ROI Analysis', 'Budget Optimization'],
      quantum: false,
      category: 'finance',
      successRate: 82
    }
  ];


  // AI-Powered Insights Generation
  useEffect(() => {
    const generateAIInsights = () => {
      const insights = [];
      
      if (dashboardMetrics.averageROI > 5) {
        insights.push({
          id: 1,
          type: 'success',
          title: 'Quantum Performance Detected',
          message: 'Your ROI exceeds industry benchmarks by 167%',
          action: () => navigate('/scenarios?tab=analyze'),
          icon: '🚀',
          confidence: 94,
          priority: 'high',
          timestamp: new Date()
        });
      }
      
      if (dashboardMetrics.scenariosCount >= 3) {
        insights.push({
          id: 2,
          type: 'info',
          title: 'Pattern Recognition Active',
          message: 'AI detected optimization patterns across your scenarios',
          action: () => navigate('/scenarios?tab=insights'),
          icon: '🔍',
          confidence: 87,
          priority: 'medium',
          timestamp: new Date()
        });
      }
      
      if (dashboardMetrics.quantumScore > 80) {
        insights.push({
          id: 3,
          type: 'warning',
          title: 'Elite Performance Tier',
          message: 'You qualify for advanced quantum analysis features',
          action: () => setQuantumMode(true),
          icon: '⚡',
          confidence: 92,
          priority: 'high',
          timestamp: new Date()
        });
      }

      if (savedScenarios.length > 0 && dashboardMetrics.averageROI < 2) {
        insights.push({
          id: 4,
          type: 'error',
          title: 'Optimization Opportunity',
          message: 'Consider reviewing your scenarios for better ROI',
          action: () => navigate('/scenarios?tab=analyze'),
          icon: '💡',
          confidence: 78,
          priority: 'medium',
          timestamp: new Date()
        });
      }

      if (credits < 3) {
        insights.push({
          id: 5,
          type: 'warning',
          title: 'Low Quantum Credits',
          message: 'Consider purchasing more credits for advanced features',
          action: () => navigate('/scenarios?tab=credits'),
          icon: '💰',
          confidence: 85,
          priority: 'medium',
          timestamp: new Date()
        });
      }

      setAiInsights(insights.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }));
    };

    generateAIInsights();
  }, [dashboardMetrics, savedScenarios, credits]);

//**********************SUPPORT FUNCTIONS - Start Mandar						*************************************************
// Load support data on component mount
useEffect(() => {
  loadSupportData();
}, []);

const loadSupportData = async () => {
  try {
    setLoadingSupport(true);
    
    // Load support tickets
    const ticketsResponse = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/support/tickets/user/${user._id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setSupportTickets(ticketsResponse.data);
    
    // Load support stats
    const statsResponse = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/support/stats/${user._id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setSupportStats(statsResponse.data);
    
  } catch (error) {
    console.error('Failed to load support data:', error);
    enqueueSnackbar('Failed to load support data', { variant: 'error' });
  } finally {
    setLoadingSupport(false);
  }
};

const handleFeatureRequest = async () => {
  if (!token) {
    enqueueSnackbar('Authentication required', { variant: 'error' });
    return;
  }

  try {
    setLoadingSupport(true);
    
    const featureData = {
      title: 'New Quantum Feature Request',
      description: 'Describe your feature idea for the quantum analysis system...',
      category: 'quantum',
      priority: 'medium',
      impact: 'high'
    };
      
    // ✅ Updated endpoint - remove the leading slash or adjust based on your route setup
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/feature-requests`, // or just '/api/feature-requests'
      {
        ...featureData,
        userId: user._id,
        userEmail: user.email,
        userName: user.name
      },
      { 
        headers: { 
          Authorization: `Bearer ${token}` 
        } 
      }
    );
      
    enqueueSnackbar('Feature request submitted successfully!', { variant: 'success' });
    setSupportDialogOpen(true);
      
  } catch (error) {
    console.error('Feature request failed:', error);
    enqueueSnackbar(
      error.response?.data?.message || 'Failed to submit feature request', 
      { variant: 'error' }
    );
  } finally {
    setLoadingSupport(false);
  }
};

const handleBugReport = async () => {
  if (!token) {
    enqueueSnackbar('Authentication required', { variant: 'error' });
    return;
  }

  try {
    setLoadingSupport(true);
    
    const bugData = {
      title: 'Quantum System Bug Report',
      description: 'Describe the issue you encountered in the quantum analysis system...',
      severity: 'medium',
      category: 'quantum',
      stepsToReproduce: '1. Navigate to...\n2. Click on...\n3. Observe...',
      expectedBehavior: 'The system should...',
      actualBehavior: 'But instead it...',
      environment: {
        browser: navigator.userAgent,
        os: 'Windows/Mac/Linux',
        device: 'Desktop'
      }
    };
      
    // ✅ Updated endpoint
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/bug-reports`, // or just '/api/bug-reports'
      {
        ...bugData,
        userId: user._id,
        userEmail: user.email,
        userName: user.name,
        userPlan: user.plan || 'standard'
      },
      { 
        headers: { 
          Authorization: `Bearer ${token}` 
        } 
      }
    );
      
    enqueueSnackbar('Bug report submitted successfully!', { variant: 'success' });
    setSupportDialogOpen(true);
      
  } catch (error) {
    console.error('Bug report failed:', error);
    enqueueSnackbar(
      error.response?.data?.message || 'Failed to submit bug report', 
      { variant: 'error' }
    );
  } finally {
    setLoadingSupport(false);
  }
};

const handleViewMyTickets = async () => {
  try {
    setLoadingSupport(true);
    
    // ✅ Add cache-busting parameter to force fresh request
    const timestamp = new Date().getTime();
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/support/tickets/user/${user._id}?t=${timestamp}`,
      { 
        headers: { 
          Authorization: `Bearer ${token}` 
        },
        // ✅ Prevent caching
        transformRequest: [(data, headers) => {
          headers['Cache-Control'] = 'no-cache';
          headers['Pragma'] = 'no-cache';
          return data;
        }]
      }
    );
    
    setSupportTickets(response.data.tickets || []);
    
    // ✅ Show actual results
    if (response.data.tickets && response.data.tickets.length > 0) {
      enqueueSnackbar(`Loaded ${response.data.tickets.length} support tickets`, { variant: 'success' });
      
      // ✅ Open a dialog to show the tickets
      setTicketsDialogOpen(true);
    } else {
      enqueueSnackbar('No support tickets found', { variant: 'info' });
    }
    
  } catch (error) {
    console.error('Failed to load tickets:', error);
    enqueueSnackbar(
      error.response?.data?.message || 'Failed to load support tickets', 
      { variant: 'error' }
    );
  } finally {
    setLoadingSupport(false);
  }
};


const handleOpenDocumentation = () => {
  // Open documentation in new tab
  window.open(`${process.env.REACT_APP_API_BASE_URL}/api/documentation/quantum`, '_blank');
  enqueueSnackbar('Opening Quantum Documentation...', { variant: 'info' });
};

const handleUpgradeSupport = async () => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/support/upgrade`,
      { userId: user._id },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    enqueueSnackbar('Support upgrade initiated! Our team will contact you shortly.', { 
      variant: 'success' 
    });
    
  } catch (error) {
    console.error('Upgrade failed:', error);
    enqueueSnackbar('Failed to initiate upgrade', { variant: 'error' });
  }
};

// Enhanced support form submission
const handleSupportSubmit = async (e) => {
  e.preventDefault();
  try {
    setLoadingSupport(true);
    
    const supportData = {
      ...supportForm,
      userId: user._id,
      userEmail: user.email,
      userName: user.name,
      userPlan: user.plan || 'standard',
      quantumMode: quantumMode,
      timestamp: new Date().toISOString(),
      status: 'open',
      ticketNumber: `QTK-${Date.now()}`,
      priority: supportForm.urgency === 'critical' ? 'high' : supportForm.urgency
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/support/tickets`,
      supportData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    enqueueSnackbar(`Quantum support ticket created: ${supportData.ticketNumber}`, { 
      variant: 'success' 
    });
    
    // Refresh support data
    await loadSupportData();
    
    setSupportForm({
      issueType: 'technical',
      subject: '',
      description: '',
      urgency: 'medium',
      attachments: []
    });
    setSupportDialogOpen(false);
    
  } catch (error) {
    console.error('Support ticket creation failed:', error);
    enqueueSnackbar('Failed to create support ticket', { variant: 'error' });
  } finally {
    setLoadingSupport(false);
  }
};

// Enhanced contact form submission
const handleContactSubmit = async (e) => {
  e.preventDefault();
  try {
    setLoadingSupport(true);
    
    const contactData = {
      ...contactForm,
      userId: user._id,
      userEmail: user.email,
      userName: user.name || contactForm.name,
      timestamp: new Date().toISOString(),
      status: 'new',
      quantum: quantumMode,
      source: 'dashboard'
    };

    await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/contact/messages`,
      contactData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    enqueueSnackbar('Message sent to quantum support team!', { variant: 'success' });
    
    setContactForm({
      name: user?.name || '',
      email: user?.email || '',
      subject: '',
      message: '',
      priority: 'medium',
      category: 'general'
    });
    setContactDialogOpen(false);
    
  } catch (error) {
    console.error('Contact message failed:', error);
    enqueueSnackbar('Failed to send message', { variant: 'error' });
  } finally {
    setLoadingSupport(false);
  }
};

// Add this dialog component before the return statement
const TicketsDialog = () => (
  <Dialog
    open={ticketsDialogOpen}
    onClose={() => setTicketsDialogOpen(false)}
    maxWidth="md"
    fullWidth
  >
    <DialogTitle>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <History sx={{ mr: 1 }} />
        My Support Tickets
        <Chip 
          label={`${supportTickets.length} tickets`} 
          size="small" 
          color="primary"
          sx={{ ml: 1 }}
        />
      </Box>
    </DialogTitle>
    <DialogContent>
      {supportTickets.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <History sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Support Tickets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You haven't created any support tickets yet.
          </Typography>
        </Box>
      ) : (
        <List>
          {supportTickets.map((ticket) => (
            <ListItem
              key={ticket._id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 1,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ 
                  bgcolor: 
                    ticket.status === 'resolved' ? 'success.main' :
                    ticket.status === 'in_progress' ? 'warning.main' : 'primary.main'
                }}>
                  {ticket.status === 'resolved' ? <CheckCircle /> :
                   ticket.status === 'in_progress' ? <History /> : <SupportAgent />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {ticket.subject}
                    </Typography>
                    <Chip 
                      label={ticket.status} 
                      size="small"
                      color={
                        ticket.status === 'resolved' ? 'success' :
                        ticket.status === 'in_progress' ? 'warning' : 'primary'
                      }
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {ticket.description.length > 100 
                        ? `${ticket.description.substring(0, 100)}...` 
                        : ticket.description
                      }
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        #{ticket.ticketNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Priority: {ticket.priority}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setTicketsDialogOpen(false)}>
        Close
      </Button>
      <Button 
        variant="contained" 
        onClick={() => {
          setTicketsDialogOpen(false);
          setSupportDialogOpen(true);
        }}
      >
        Create New Ticket
      </Button>
    </DialogActions>
  </Dialog>
);

//***********************************************************************

  // Enhanced Quick Actions with Quantum Navigation
  const quickActions = [
    {
      title: 'Quantum Analysis',
      description: 'AI-powered deep analysis',
      icon: <RocketLaunch />,
      color: 'primary',
      action: () => {
        setSelectedScenario(null);
        navigate('/scenarios?tab=analyze&mode=quantum');
        enqueueSnackbar('🚀 Initializing quantum analysis matrix...', { 
          variant: 'info',
          autoHideDuration: 2000 
        });
      },
      hotkey: 'Q',
      badge: credits > 0 ? 'AI' : 'No Credits',
      disabled: credits <= 0,
      premium: true,
      quantum: true
    },
    {
      title: 'Template Analysis',
      description: 'Quick start with templates',
      icon: <PlayArrow />,
      color: 'secondary',
      action: () => {
        if (credits > 0) {
          setQuickAnalysisDialogOpen(true);
          enqueueSnackbar('🌀 Loading quantum templates...', { 
            variant: 'info',
            autoHideDuration: 1500 
          });
        } else {
          enqueueSnackbar('Insufficient quantum credits', { 
            variant: 'warning',
            autoHideDuration: 4000 
          });
        }
      },
      hotkey: 'T',
      badge: 'Templates',
      disabled: credits <= 0,
      premium: false
    },
    {
      title: 'Multi-Dimensional Compare',
      description: 'Advanced scenario comparison',
      icon: <CompareArrows />,
      color: 'secondary',
      action: () => {
        if (savedScenarios.length >= 2) {
          navigate('/scenarios?tab=scenarios&mode=comparison');
          enqueueSnackbar('🔬 Loading multi-dimensional analysis...', { 
            variant: 'info',
            autoHideDuration: 3000 
          });
        } else {
          enqueueSnackbar('Need at least 2 scenarios for quantum comparison', { 
            variant: 'warning',
            autoHideDuration: 4000 
          });
        }
      },
      hotkey: 'C',
      disabled: savedScenarios.length < 2,
      premium: true
    },
    {
      title: 'Predictive Insights',
      description: 'AI-driven recommendations',
      icon: <PsychologyIcon />,
      color: 'info',
      action: () => {
        navigate('/scenarios?tab=insights&mode=ai');
        enqueueSnackbar('🧠 Generating intelligent insights...', { 
          variant: 'info',
          autoHideDuration: 2500 
        });
      },
      hotkey: 'I',
      premium: false
    },
    {
      title: 'Scenario Nexus',
      description: 'Manage all scenarios',
      icon: <ViewList />,
      color: 'primary',
      action: () => {
        navigate('/scenarios?tab=scenarios');
        enqueueSnackbar('📊 Accessing scenario nexus...', { 
          variant: 'info',
          autoHideDuration: 2000 
        });
      },
      hotkey: 'N',
      premium: false
    },
    {
      title: 'Quantum Export',
      description: 'Advanced reporting',
      icon: <FileDownload />,
      color: 'success',
      action: () => {
        if (savedScenarios.length === 0) {
          enqueueSnackbar('Create scenarios to unlock quantum reporting', { 
            variant: 'warning',
            autoHideDuration: 4000 
          });
          return;
        }
        setExportDialogOpen(true);
      },
      hotkey: 'E',
      premium: true
    }
  ];

  // Enhanced User Achievements with Quantum Badges
  const userAchievements = [
    { 
      name: 'Quantum Pioneer', 
      icon: '🚀', 
      unlocked: savedScenarios.length > 0,
      action: () => navigate('/scenarios?tab=analyze'),
      description: 'Completed first quantum analysis',
      tier: 'bronze',
      points: 100,
      progress: savedScenarios.length > 0 ? 100 : 0
    },
    { 
      name: 'ROI Visionary', 
      icon: '💰', 
      unlocked: dashboardMetrics.averageROI > 5,
      action: () => navigate('/scenarios?tab=insights'),
      description: 'Achieved 5x+ ROI in any scenario',
      tier: 'silver',
      points: 250,
      progress: Math.min(100, (dashboardMetrics.averageROI / 5) * 100)
    },
    { 
      name: 'Scenario Architect', 
      icon: '🏗️', 
      unlocked: savedScenarios.length >= 5,
      action: () => navigate('/scenarios?tab=scenarios'),
      description: 'Built 5+ quantum scenarios',
      tier: 'gold',
      points: 500,
      progress: Math.min(100, (savedScenarios.length / 5) * 100)
    },
    { 
      name: 'Impact Titan', 
      icon: '⚡', 
      unlocked: dashboardMetrics.totalImpact > 5000000,
      action: () => {
        const highImpact = savedScenarios.reduce((max, scenario) => 
          (scenario.totalImpact || 0) > (max.totalImpact || 0) ? scenario : max
        );
        navigate(`/scenarios?tab=analyze&scenario=${highImpact._id}`);
      },
      description: 'Generated 5M+ in total impact',
      tier: 'platinum',
      points: 1000,
      progress: Math.min(100, (dashboardMetrics.totalImpact / 5000000) * 100)
    },
    { 
      name: 'Quantum Master', 
      icon: '🌌', 
      unlocked: dashboardMetrics.quantumScore > 90,
      action: () => setQuantumMode(true),
      description: 'Achieved elite quantum score',
      tier: 'diamond',
      points: 2000,
      progress: dashboardMetrics.quantumScore
    }
  ];

  // Enhanced Performance Trends
  const performanceTrends = useMemo(() => {
    const monthlyData = savedScenarios.reduce((acc, scenario) => {
      const month = new Date(scenario.createdAt).toLocaleDateString('en-US', { month: 'short' });
      if (!acc[month]) acc[month] = { impact: 0, count: 0, roi: 0 };
      acc[month].impact += scenario.totalImpact || 0;
      acc[month].count += 1;
      acc[month].roi += calculateROI(scenario);
      return acc;
    }, {});

    return Object.entries(monthlyData).map(([month, data]) => ({
      month,
      impact: data.impact,
      count: data.count,
      avgROI: data.roi / data.count,
      trend: data.impact > 1000000 ? 'up' : data.impact > 500000 ? 'stable' : 'down'
    }));
  }, [savedScenarios]);

  // Enhanced Navigation Handlers
  const handleViewScenario = useCallback((scenarioId) => {
    const scenario = savedScenarios.find(s => s._id === scenarioId);
    if (scenario) {
      setSelectedScenario(scenario);
      navigate(`/scenarios?tab=analyze&scenario=${scenarioId}&view=quantum`);
      enqueueSnackbar(`🔍 Loading ${scenario.name} in quantum view...`, { 
        variant: 'success',
        autoHideDuration: 2000 
      });
    }
  }, [savedScenarios, enqueueSnackbar, navigate]);

  const handleQuantumAnalysis = useCallback(() => {
    setQuantumMode(true);
    navigate('/scenarios?tab=analyze&mode=quantum');
    enqueueSnackbar('🌌 Entering quantum analysis mode...', {
      variant: 'info',
      autoHideDuration: 3000
    });
  }, [navigate, enqueueSnackbar]);

  // Enhanced Template Handler
  const handleTemplateSelect = useCallback((template) => {
    if (credits < template.credits) {
      enqueueSnackbar(`Insufficient credits. Need ${template.credits} quantum credits.`, {
        variant: 'warning',
        autoHideDuration: 4000
      });
      return;
    }

    enqueueSnackbar(`🚀 Launching ${template.name} quantum analysis...`, {
      variant: 'success',
      autoHideDuration: 2000
    });

    // Store template data for the analysis page
    localStorage.setItem('quantumTemplate', JSON.stringify(template));
    setQuickAnalysisDialogOpen(false);
    navigate('/scenarios?tab=analyze&template=quantum');
  }, [credits, enqueueSnackbar, navigate]);

  // Enhanced Export Handlers
  const handleExportScenarioToggle = useCallback((scenarioId) => {
    setExportState(prev => ({
      ...prev,
      selectedScenarios: prev.selectedScenarios.includes(scenarioId)
        ? prev.selectedScenarios.filter(id => id !== scenarioId)
        : [...prev.selectedScenarios, scenarioId]
    }));
  }, []);

  const handleExportAllScenarios = useCallback(() => {
    setExportState(prev => ({
      ...prev,
      selectedScenarios: prev.selectedScenarios.length === savedScenarios.length 
        ? [] 
        : savedScenarios.map(s => s._id)
    }));
  }, [savedScenarios]);

  // Enhanced PDF Export Functionality
  const generatePDFExport = useCallback(async () => {
    const scenariosToExport = exportState.selectedScenarios.length > 0
      ? savedScenarios.filter(s => exportState.selectedScenarios.includes(s._id))
      : savedScenarios;

    if (scenariosToExport.length === 0) {
      enqueueSnackbar('No scenarios selected for export', { variant: 'warning' });
      return;
    }

    enqueueSnackbar('🔄 Generating quantum PDF report...', { variant: 'info' });

    try {
      // Simulate PDF generation
      setActiveExportStep(1);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setActiveExportStep(2);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Create a blob and download link for PDF
      const pdfData = {
        title: `Quantum Analysis Report - ${new Date().toLocaleDateString()}`,
        scenarios: scenariosToExport,
        metrics: dashboardMetrics,
        generatedAt: new Date().toISOString(),
        user: user?.email,
        reportType: exportState.reportType,
        includeCharts: exportState.includeCharts,
        quantumMode: quantumMode
      };

      const dataStr = JSON.stringify(pdfData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quantum-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setActiveExportStep(3);
      await new Promise(resolve => setTimeout(resolve, 500));

      enqueueSnackbar(`✅ PDF report generated for ${scenariosToExport.length} scenarios!`, {
        variant: 'success',
        autoHideDuration: 3000
      });

      setExportDialogOpen(false);
      setActiveExportStep(0);
    } catch (error) {
      enqueueSnackbar('Failed to generate PDF report', { variant: 'error' });
      setActiveExportStep(0);
    }
  }, [exportState, savedScenarios, dashboardMetrics, user, quantumMode]);

  // Enhanced CSV Export
  const generateCSVExport = useCallback(() => {
    const scenariosToExport = exportState.selectedScenarios.length > 0
      ? savedScenarios.filter(s => exportState.selectedScenarios.includes(s._id))
      : savedScenarios;

    if (scenariosToExport.length === 0) {
      enqueueSnackbar('No scenarios selected for export', { variant: 'warning' });
      return;
    }

    const headers = ['Scenario Name', 'Industry', 'Total Impact', 'Turnover', 'ROI', 'Created Date', 'Status', 'Tier'];
    const csvRows = [headers.join(',')];
    
    scenariosToExport.forEach(scenario => {
      const row = [
        `"${scenario.name}"`,
        `"${scenario.industry}"`,
        scenario.totalImpact || 0,
        scenario.turnover || 0,
        calculateROI(scenario).toFixed(2),
        new Date(scenario.createdAt).toLocaleDateString(),
        scenario.status || 'active',
        scenario.tier || 'standard'
      ];
      csvRows.push(row.join(','));
    });
    
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `quantum-scenarios-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    enqueueSnackbar(`📊 CSV export completed for ${scenariosToExport.length} scenarios!`, {
      variant: 'success'
    });
  }, [exportState, savedScenarios]);


  // Enhanced Scenario Management Handlers
  const handleDuplicateScenario = useCallback(async (scenarioId) => {
    try {
      await duplicateScenario(scenarioId);
      enqueueSnackbar('🌀 Scenario duplicated with quantum precision!', { variant: 'success' });
      setScenarioMenuAnchor(null);
    } catch (error) {
      enqueueSnackbar('Failed to duplicate scenario', { variant: 'error' });
    }
  }, [duplicateScenario, enqueueSnackbar]);

  const handleDeleteScenario = useCallback(async (scenarioId) => {
    if (window.confirm('Are you sure you want to delete this quantum scenario?')) {
      try {
        await deleteScenario(scenarioId);
        enqueueSnackbar('Scenario deleted from quantum database', { variant: 'success' });
        setScenarioMenuAnchor(null);
      } catch (error) {
        enqueueSnackbar('Failed to delete scenario', { variant: 'error' });
      }
    }
  }, [deleteScenario, enqueueSnackbar]);

  const handleNotificationClick = useCallback((notification) => {
    if (notification.action) {
      notification.action();
    }
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );
    setNotificationCenterOpen(false);
  }, []);

  const handleUserAction = useCallback((action) => {
    switch (action) {
      case 'profile':
        navigate('/profile');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'billing':
        navigate('/scenarios?tab=credits');
        break;
      case 'support':
        setSupportDialogOpen(true);
        break;
      case 'contact':
        setContactDialogOpen(true);
        break;
      case 'logout':
        logout();
        break;
      default:
        break;
    }
    setUserMenuAnchor(null);
  }, [navigate, logout]);

  // Enhanced Metrics Component with Quantum Visualization
  const QuantumMetricCard = ({ metric, index }) => (
    <Grid item xs={12} sm={6} md={3} key={metric.title}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        whileHover={{ scale: 1.02 }}
      >
        <HolographicCard 
          sx={{ 
            cursor: 'pointer',
            background: metric.premium ? 
              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
              'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #fff, transparent)',
              animation: 'shine 2s infinite'
            },
            '@keyframes shine': {
              '0%': { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(100%)' }
            }
          }}
          onClick={metric.action}
        >
          <CardContent sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
            {/* Premium Badge */}
            {metric.premium && (
              <Chip 
                label="QUANTUM" 
                size="small" 
                sx={{ 
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.6rem',
                  height: 20
                }}
              />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                width: 60, 
                height: 60,
                backdropFilter: 'blur(10px)'
              }}>
                {metric.icon}
              </Avatar>
            </Box>
            
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.8rem' }}>
              <DynamicGradientText>
                <AnimatedCounter 
                  value={metric.value} 
                  duration={2000}
                  formattingFn={metric.format}
                />
              </DynamicGradientText>
            </Typography>
            
            <Typography variant="h6" sx={{ mb: 1, opacity: 0.9 }}>
              {metric.title}
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
              {metric.subtitle}
            </Typography>

            {/* Enhanced Progress Visualization */}
            {metric.progress && (
              <Box sx={{ position: 'relative', mb: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={metric.progress}
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'white',
                      borderRadius: 4
                    }
                  }}
                />
                <Typography variant="caption" sx={{ position: 'absolute', right: 0, top: -20 }}>
                  {metric.progress.toFixed(0)}%
                </Typography>
              </Box>
            )}

            {/* Trend Indicator */}
            {metric.trend && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 2,
                p: 1,
                mt: 1
              }}>
                <TrendingUp 
                  fontSize="small" 
                  sx={{ 
                    transform: metric.trend === 'down' ? 'rotate(180deg)' : 'none',
                    mr: 0.5
                  }} 
                />
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {metric.trendVelocity}
                </Typography>
              </Box>
            )}
          </CardContent>
        </HolographicCard>
      </motion.div>
    </Grid>
  );

  // Enhanced Export Dialog Component
  const QuantumExportDialog = () => (
    <Dialog
      open={exportDialogOpen}
      onClose={() => {
        setExportDialogOpen(false);
        setActiveExportStep(0);
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CloudDownload sx={{ mr: 1 }} />
            Quantum Export
          </Box>
          <Chip label="QUANTUM" color="primary" size="small" />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Tabs value={exportTab} onChange={(e, newValue) => setExportTab(newValue)} sx={{ mb: 3 }}>
          <Tab icon={<TableChart />} label="Scenarios" value="scenarios" />
          <Tab icon={<BarChart />} label="Analytics" value="analytics" />
          <Tab icon={<PictureAsPdf />} label="Reports" value="reports" />
        </Tabs>

        {exportTab === 'scenarios' && (
          <Box>
            <Typography gutterBottom>
              Export your quantum scenarios in multiple formats
            </Typography>
            
            {/* Export Format Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Export Format:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[
                  { value: 'pdf', label: 'PDF Report', icon: <PictureAsPdf /> },
                  { value: 'csv', label: 'CSV Data', icon: <TableChart /> },
                  { value: 'json', label: 'JSON', icon: <DataObject /> },
                  { value: 'excel', label: 'Excel', icon: <TableChart /> }
                ].map((format) => (
                  <Chip
                    key={format.value}
                    icon={format.icon}
                    label={format.label}
                    onClick={() => setExportState(prev => ({ ...prev, exportFormat: format.value }))}
                    color={exportState.exportFormat === format.value ? 'primary' : 'default'}
                    variant={exportState.exportFormat === format.value ? 'filled' : 'outlined'}
                    clickable
                  />
                ))}
              </Box>
            </Box>

            {/* Scenario Selection */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Select Scenarios ({exportState.selectedScenarios.length} of {savedScenarios.length} selected)
                </Typography>
                <Button size="small" onClick={handleExportAllScenarios}>
                  {exportState.selectedScenarios.length === savedScenarios.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
              
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p: 2 }}>
                {savedScenarios.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No scenarios available for export
                  </Typography>
                ) : (
                  <Grid container spacing={1}>
                    {savedScenarios.map((scenario) => (
                      <Grid item xs={12} key={scenario._id}>
                        <Paper
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            bgcolor: exportState.selectedScenarios.includes(scenario._id) 
                              ? 'primary.light' 
                              : 'background.default',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              bgcolor: exportState.selectedScenarios.includes(scenario._id)
                                ? 'primary.main'
                                : 'action.hover'
                            }
                          }}
                          onClick={() => handleExportScenarioToggle(scenario._id)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                              checked={exportState.selectedScenarios.includes(scenario._id)}
                              sx={{ mr: 2 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1">{scenario.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {scenario.industry} • {currencySymbol}{((scenario.totalImpact || 0)/1000).toFixed(0)}K • ROI: {calculateROI(scenario).toFixed(1)}x
                              </Typography>
                            </Box>
                            <Chip 
                              label={scenario.tier} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            </Box>

            {/* Export Options */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Export Options:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={exportState.includeCharts}
                        onChange={(e) => setExportState(prev => ({ ...prev, includeCharts: e.target.checked }))}
                      />
                    }
                    label="Include Charts & Visualizations"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={exportState.includeRawData}
                        onChange={(e) => setExportState(prev => ({ ...prev, includeRawData: e.target.checked }))}
                      />
                    }
                    label="Include Raw Data"
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {exportTab === 'reports' && (
          <Box>
            <Typography gutterBottom>
              Generate comprehensive quantum analysis reports
            </Typography>
            
            <Stepper activeStep={activeExportStep} orientation="vertical" sx={{ mb: 3 }}>
              <Step>
                <StepLabel>Preparing Quantum Data</StepLabel>
                <StepContent>
                  <Typography>Collecting scenario data and AI insights...</Typography>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>Generating Report</StepLabel>
                <StepContent>
                  <Typography>Creating PDF with charts and analysis...</Typography>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>Finalizing Export</StepLabel>
                <StepContent>
                  <Typography>Preparing download...</Typography>
                </StepContent>
              </Step>
            </Stepper>

            <Box sx={{ textAlign: 'center', py: 2 }}>
              <PictureAsPdf sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Quantum PDF Report
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Professional report with AI insights, predictive analytics, and executive summaries
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={exportState.reportType}
                  label="Report Type"
                  onChange={(e) => setExportState(prev => ({ ...prev, reportType: e.target.value }))}
                >
                  <MenuItem value="executive">Executive Summary</MenuItem>
                  <MenuItem value="detailed">Detailed Analysis</MenuItem>
                  <MenuItem value="technical">Technical Report</MenuItem>
                  <MenuItem value="quantum">Quantum Insights</MenuItem>
                </Select>
              </FormControl>

              <Button 
                variant="contained" 
                size="large"
                startIcon={<PictureAsPdf />}
                onClick={generatePDFExport}
                disabled={savedScenarios.length === 0 || activeExportStep > 0}
                sx={{ mb: 2 }}
              >
                {activeExportStep > 0 ? 'Generating Report...' : 'Generate Quantum PDF Report'}
              </Button>
              
              {activeExportStep > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Processing {exportState.selectedScenarios.length || savedScenarios.length} scenarios...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {exportTab === 'analytics' && (
          <Box>
            <Typography gutterBottom>
              Export analytics and performance data
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Export detailed analytics including trends, predictions, and AI insights
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <TableChart sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Performance Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Export scenario performance metrics and trends
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={generateCSVExport}
                    disabled={savedScenarios.length === 0}
                  >
                    Export as CSV
                  </Button>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <BarChart sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Analytics Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Export comprehensive analytics and insights
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={generatePDFExport}
                    disabled={savedScenarios.length === 0}
                  >
                    Export as PDF
                  </Button>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={() => {
            setExportDialogOpen(false);
            setActiveExportStep(0);
          }}
          disabled={activeExportStep > 0}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={exportState.exportFormat === 'pdf' ? generatePDFExport : generateCSVExport}
          startIcon={activeExportStep > 0 ? <CircularProgress size={16} /> : <Download />}
          disabled={savedScenarios.length === 0 || activeExportStep > 0}
        >
          {activeExportStep > 0 ? 'Exporting...' : `Export ${exportState.selectedScenarios.length || 'All'} as ${exportState.exportFormat.toUpperCase()}`}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Enhanced Quick Analysis Dialog
  const QuantumQuickAnalysisDialog = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');

    const filteredTemplates = quantumTemplates.filter(template => 
      selectedCategory === 'all' || template.category === selectedCategory
    );

    return (
      <Dialog
        open={quickAnalysisDialogOpen}
        onClose={() => setQuickAnalysisDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RocketLaunch sx={{ mr: 1 }} />
              Quantum Analysis Templates
            </Box>
            <Chip 
              label={`${credits} Quantum Credits`} 
              color={credits > 0 ? "success" : "error"}
              size="small"
            />
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography gutterBottom color="text.secondary">
            Start with AI-powered quantum analysis templates
          </Typography>
          
          {/* Category Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Filter by Category:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { value: 'all', label: 'All Templates' },
                { value: 'operations', label: 'Operations' },
                { value: 'strategy', label: 'Strategy' },
                { value: 'finance', label: 'Finance' },
                { value: 'marketing', label: 'Marketing' }
              ].map((category) => (
                <Chip
                  key={category.value}
                  label={category.label}
                  onClick={() => setSelectedCategory(category.value)}
                  color={selectedCategory === category.value ? 'primary' : 'default'}
                  variant={selectedCategory === category.value ? 'filled' : 'outlined'}
                  clickable
                />
              ))}
            </Box>
          </Box>

          <Grid container spacing={3}>
            {filteredTemplates.map((template) => (
              <Grid item xs={12} md={6} key={template.id}>
                <motion.div whileHover={{ scale: 1.02 }}>
                  <Card
                    sx={{
                      p: 3,
                      cursor: credits >= template.credits ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                      border: '2px solid',
                      borderColor: template.quantum ? 'primary.main' : 'divider',
                      background: template.quantum ? 
                        'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' : 'background.paper',
                      opacity: credits >= template.credits ? 1 : 0.6,
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': credits >= template.credits ? {
                        borderColor: 'primary.main',
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8]
                      } : {}
                    }}
                    onClick={() => credits >= template.credits && handleTemplateSelect(template)}
                  >
                    {/* Quantum Badge */}
                    {template.quantum && (
                      <Chip 
                        label="QUANTUM AI" 
                        size="small" 
                        color="primary"
                        sx={{ 
                          position: 'absolute',
                          top: 12,
                          right: 12
                        }}
                      />
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: template.quantum ? 'primary.main' : 'secondary.main', 
                        width: 50, 
                        height: 50,
                        mr: 2
                      }}>
                        {template.icon}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {template.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {template.industry} • {template.estimatedTime}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.description}
                    </Typography>

                    {/* Features */}
                    <Box sx={{ mb: 2 }}>
                      {template.features.map((feature, index) => (
                        <Chip
                          key={feature}
                          label={feature}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={template.impact} 
                          size="small" 
                          color={
                            template.impact === 'Very High' ? 'error' :
                            template.impact === 'High' ? 'warning' : 'success'
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          {template.successRate}% success
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CreditScore fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2" fontWeight="bold">
                          {template.credits}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Insufficient Credits Message */}
                    {credits < template.credits && (
                      <Alert severity="warning" sx={{ mt: 2, py: 0 }}>
                        Need {template.credits - credits} more quantum credits
                      </Alert>
                    )}
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setQuickAnalysisDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setQuickAnalysisDialogOpen(false);
              navigate('/scenarios?tab=analyze');
            }}
            startIcon={<Add />}
            variant="outlined"
          >
            Custom Quantum Analysis
          </Button>
          <Button 
            onClick={() => navigate('/scenarios?tab=credits')}
            startIcon={<CreditScore />}
            variant="contained"
          >
            Get More Credits
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Recent Scenarios with enhanced data
  const recentScenarios = useMemo(() => {
    return savedScenarios
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5)
      .map(scenario => ({
        ...scenario,
        status: (scenario.totalImpact || 0) > 2000000 ? 'high' : 
                (scenario.totalImpact || 0) > 1000000 ? 'medium' : 'low',
        progress: Math.min(100, ((scenario.totalImpact || 0) / 5000000) * 100),
        roi: calculateROI(scenario)
      }));
  }, [savedScenarios]);

  // Contact & Support Options
  const contactOptions = [
    {
      title: 'Quantum Support',
      description: 'Get help with quantum features',
      icon: <SupportAgent />,
      action: () => setSupportDialogOpen(true),
      quantum: true
    },
    {
      title: 'Sales Inquiry',
      description: 'Learn about enterprise solutions',
      icon: <Business />,
      action: () => setContactDialogOpen(true)
    },
    {
      title: 'Feature Request',
      description: 'Suggest new quantum features',
      icon: <Lightbulb />,
      action: () => setContactDialogOpen(true),
      quantum: true
    },
    {
      title: 'Bug Report',
      description: 'Report quantum system issues',
      icon: <BugReport />,
      action: () => setSupportDialogOpen(true),
      quantum: true
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3, position: 'relative' }}>
      <QuantumParticlesEnhanced quantumMode={quantumMode} />
      
      {/* Quantum Mode Toggle */}
      <Box sx={{ position: 'fixed', top: 200, right: 24, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Demo Button */}
        <Tooltip title="How to Use ROIAnalyzer - Interactive Demo">
          <IconButton
            onClick={() => setDemoDialogOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
              color: 'white',
              width: 56,
              height: 56,
              '&:hover': {
                transform: 'scale(1.1)',
                background: 'linear-gradient(135deg, #45a049 0%, #4CAF50 100%)',
                boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)'
              }
            }}
          >
            <PlayArrow />
          </IconButton>
        </Tooltip>

        {/* Quantum Mode Toggle (existing) */}
        <Tooltip title={quantumMode ? "Exit Quantum Mode" : "Enter Quantum Mode"}>
          <IconButton
            onClick={() => setQuantumMode(!quantumMode)}
            sx={{
              background: quantumMode 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              width: 56,
              height: 56,
              '&:hover': {
                transform: 'scale(1.1)',
                background: quantumMode 
                  ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                  : 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)'
              }
            }}
          >
            {quantumMode ? <Biotech /> : <Science />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Quantum Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, type: "spring" }}
      >
        <HolographicCard sx={{ 
		  mt: 9,
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Animated Background Elements */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%)
              `,
              animation: 'pulse 4s ease-in-out infinite alternate',
              '@keyframes pulse': {
                '0%': { opacity: 0.3 },
                '100%': { opacity: 0.6 }
              }
            }}
          />
          
          <CardContent sx={{ color: 'white', py: 4, position: 'relative', zIndex: 2, pt: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <RocketLaunch sx={{ fontSize: '2.5rem' }} />
                    Quantum Dashboard
                    <Chip 
                      label="AI-POWERED" 
                      size="small" 
                      sx={{ 
                        background: 'rgba(255,255,255,0.2)', 
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Typography>
                
                <Typography variant="h5" sx={{ opacity: 0.9, mb: 3 }}>
                  Welcome back, <strong>{user?.name || user?.email?.split('@')[0]}</strong>! 
                  {dashboardMetrics.quantumScore > 80 ? 
                    " 🚀 Quantum performance detected!" : 
                    " Ready for quantum optimization?"
                  }
                </Typography>
                
                {/* Enhanced Stats Bar - ADDED DEMO BUTTON */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={() => setDemoDialogOpen(true)}
                    sx={{
                      background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      borderRadius: 3,
                      px: 3,
                      py: 1,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #45a049 0%, #4CAF50 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(76, 175, 80, 0.4)'
                      }
                    }}
                  >
                    How to Use Demo
                  </Button>
                  
                  <Chip 
                    icon={<CreditScore />} 
                    label={`${credits} Quantum Credits`}
                    onClick={() => navigate('/scenarios?tab=credits')}
                    clickable
                    sx={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 'bold',
                      backdropFilter: 'blur(10px)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' }
                    }}
                  />
                  <Chip 
                    icon={<BarChart />} 
                    label={`${dashboardMetrics.scenariosCount} Quantum Scenarios`}
                    onClick={() => navigate('/scenarios?tab=scenarios')}
                    clickable
                    sx={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 'bold',
                      backdropFilter: 'blur(10px)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' }
                    }}
                  />
                  <Chip 
                    icon={<TrendingUp />} 
                    label={`${currencySymbol}${(dashboardMetrics.totalImpact/1000000).toFixed(1)}M Quantum Impact`}
                    onClick={() => navigate('/scenarios?tab=analyze')}
                    clickable
                    sx={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 'bold',
                      backdropFilter: 'blur(10px)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' }
                    }}
                  />
                  {dashboardMetrics.quantumScore > 70 && (
                    <Chip 
                      icon={<WorkspacePremium />} 
                      label={`Quantum Score: ${dashboardMetrics.quantumScore}`}
                      onClick={handleQuantumAnalysis}
                      clickable
                      sx={{ 
                        background: 'linear-gradient(45deg, #FFD700, #FFA500)',
                        color: 'black',
                        fontWeight: 'bold',
                        '&:hover': { background: 'linear-gradient(45deg, #FFA500, #FFD700)' }
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Enhanced User Actions */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title="Quantum Support">
                  <IconButton 
                    sx={{ color: 'white', background: 'rgba(255,255,255,0.1)' }}
                    onClick={() => setSupportDialogOpen(true)}
                  >
                    <SmartToy />
                  </IconButton>
                </Tooltip>
                <Tooltip title="AI Insights">
                  <IconButton 
                    sx={{ color: 'white', background: 'rgba(255,255,255,0.1)' }}
                    onClick={() => navigate('/scenarios?tab=insights')}
                  >
                    <Psychology />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Quantum Notifications">
                  <IconButton 
                    sx={{ color: 'white', background: 'rgba(255,255,255,0.1)' }}
                    onClick={() => setNotificationCenterOpen(true)}
                  >
                    <Badge badgeContent={notifications.filter(n => !n.read).length} color="error">
                      <Notifications />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Quantum Profile">
                  <IconButton 
                    sx={{ color: 'white' }}
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                  >
                    <Avatar 
                      sx={{ 
                        width: 48, 
                        height: 48,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        border: '2px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      <Person />
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </HolographicCard>
      </motion.div>


      {/* AI Insights Alert */}
      {aiInsights.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => navigate('/scenarios?tab=insights')}
              >
                VIEW INSIGHTS
              </Button>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Psychology sx={{ fontSize: 20 }} />
              <strong>AI detected {aiInsights.length} optimization opportunities</strong>
            </Box>
          </Alert>
        </motion.div>
      )}

      {/* Quantum Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            title: 'Quantum Impact',
            value: dashboardMetrics.totalImpact,
            format: (val) => `${currencySymbol}${(val/1000000).toFixed(1)}M`,
            subtitle: 'Total Value Generated',
            icon: <TrendingUp />,
            action: () => navigate('/scenarios?tab=analyze'),
            progress: Math.min(100, (dashboardMetrics.totalImpact / 10000000) * 100),
            trend: dashboardMetrics.monthlyTrend,
            trendVelocity: dashboardMetrics.trendVelocity,
            premium: true
          },
          {
            title: 'Predictive ROI',
            value: dashboardMetrics.predictiveROI,
            format: (val) => val.toFixed(1) + 'x',
            subtitle: 'AI Projected Returns',
            icon: <AccountBalance />,
            action: () => navigate('/scenarios?tab=insights'),
            badge: dashboardMetrics.averageROI > 5 ? "QUANTUM" : "OPTIMAL",
            premium: true
          },
          {
            title: 'Quantum Score',
            value: dashboardMetrics.quantumScore,
            format: (val) => val.toFixed(0),
            subtitle: 'Performance Intelligence',
            icon: <Speed />,
            action: () => handleQuantumAnalysis(),
            progress: dashboardMetrics.quantumScore,
            premium: true
          },
          {
            title: 'Efficiency Gain',
            value: dashboardMetrics.efficiencyGain,
            format: (val) => val.toFixed(0) + '%',
            subtitle: 'Process Optimization',
            icon: <Analytics />,
            action: () => navigate('/scenarios?tab=analyze&view=efficiency'),
            progress: dashboardMetrics.efficiencyGain,
            premium: false
          }
        ].map((metric, index) => (
          <QuantumMetricCard key={metric.title} metric={metric} index={index} />
        ))}
      </Grid>

      {/* Main Content Area */}
      <Grid container spacing={3}>
        {/* Quick Actions & Recent Scenarios */}
        <Grid item xs={12} md={8}>
          {/* Quantum Quick Actions */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <HolographicCard sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <Whatshot sx={{ mr: 1, color: 'orange' }} />
                    Quantum Actions
                    <Chip 
                      label="AI-ENHANCED" 
                      size="small" 
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Voice commands available • Say "Hey Quantum"
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {quickActions.map((action, index) => (
                    <Grid item xs={12} sm={6} md={4} key={action.title}>
                      <motion.div 
                        whileHover={{ scale: 1.05, y: -5 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 400, damping: 17 }}
                      >
                        <Button
                          fullWidth
                          variant={action.premium ? "contained" : "outlined"}
                          startIcon={action.icon}
                          onClick={action.action}
                          disabled={action.disabled}
                          sx={{
                            height: 100,
                            flexDirection: 'column',
                            borderRadius: 3,
                            borderWidth: action.premium ? 0 : 2,
                            background: action.premium ? 
                              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: action.premium ? theme.shadows[8] : theme.shadows[4]
                            }
                          }}
                        >
                          {action.badge && (
                            <Chip 
                              label={action.badge} 
                              size="small" 
                              color="secondary"
                              sx={{ 
                                position: 'absolute', 
                                top: -8, 
                                right: -8,
                                fontSize: '0.6rem',
                                height: 20
                              }}
                            />
                          )}
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                            {action.title}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {action.description}
                          </Typography>
                          {action.hotkey && (
                            <Box 
                              sx={{ 
                                position: 'absolute', 
                                bottom: 8, 
                                right: 8,
                                fontSize: '0.7rem',
                                opacity: 0.6
                              }}
                            >
                              ⌘{action.hotkey}
                            </Box>
                          )}
                          {action.premium && (
                            <Box 
                              sx={{ 
                                position: 'absolute', 
                                top: 8, 
                                left: 8,
                                fontSize: '0.6rem',
                                opacity: 0.8
                              }}
                            >
                              ⚡
                            </Box>
                          )}
                        </Button>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </HolographicCard>
          </motion.div>

          {/* Recent Quantum Scenarios */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <HolographicCard>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <Timeline sx={{ mr: 1 }} />
                    Recent Quantum Scenarios
                    <Chip 
                      label={`${savedScenarios.length} TOTAL`} 
                      size="small" 
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      size="small" 
                      onClick={() => navigate('/scenarios?tab=scenarios')}
                      endIcon={<ViewList />}
                      variant="outlined"
                    >
                      Scenario Nexus
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => {
                        navigate('/scenarios?tab=analyze');
                        setSelectedScenario(null);
                        enqueueSnackbar('🌌 Initializing quantum scenario creation...', { variant: 'info' });
                      }}
                      startIcon={<Add />}
                      variant="contained"
                    >
                      New Quantum
                    </Button>
                  </Box>
                </Box>

                {savedScenarios.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No quantum scenarios yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Start your quantum optimization journey
                    </Typography>
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={() => {
                        navigate('/scenarios?tab=analyze');
                        setSelectedScenario(null);
                      }}
                      startIcon={<RocketLaunch />}
                      sx={{ borderRadius: 3 }}
                    >
                      Launch First Quantum Analysis
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {recentScenarios.map((scenario, index) => (
                      <motion.div
                        key={scenario._id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <Paper 
                          sx={{ 
                            p: 2, 
                            mb: 2, 
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            '&:hover': {
                              transform: 'translateX(8px)',
                              boxShadow: theme.shadows[8],
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white'
                            }
                          }}
                          onClick={() => handleViewScenario(scenario._id)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setSelectedScenario(scenario);
                            setScenarioMenuAnchor(e.currentTarget);
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 1 }}>
                                  {scenario.name}
                                </Typography>
                                <Chip 
                                  label={scenario.industry} 
                                  size="small" 
                                  variant="outlined"
                                />
                                <Chip 
                                  label={`ROI: ${scenario.roi.toFixed(1)}x`}
                                  size="small"
                                  color={
                                    scenario.roi > 5 ? 'success' :
                                    scenario.roi > 3 ? 'warning' : 'error'
                                  }
                                />
                              </Box>
                              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                {new Date(scenario.createdAt).toLocaleDateString()} • {scenario.tier}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="h6" fontWeight="bold" color="primary">
                                {currencySymbol}{((scenario.totalImpact || 0)/1000).toFixed(0)}K
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                Quantum Impact
                              </Typography>
                            </Box>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={scenario.progress}
                            color="primary"
                            sx={{ mt: 1, height: 6, borderRadius: 3 }}
                          />
                        </Paper>
                      </motion.div>
                    ))}
                  </Box>
                )}
              </CardContent>
            </HolographicCard>
          </motion.div>
        </Grid>

        {/* Quantum Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Quantum Achievements */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <HolographicCard sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <MilitaryTech sx={{ mr: 1, color: 'gold' }} />
                  Quantum Achievements
                  <Chip 
                    label={`${userAchievements.filter(a => a.unlocked).length}/${userAchievements.length}`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Grid container spacing={1}>
                  {userAchievements.map((achievement, index) => (
                    <Grid item xs={6} key={achievement.name}>
                      <Tooltip title={`${achievement.description} (${achievement.points} points)`} arrow>
                        <Paper
                          sx={{
                            p: 1.5,
                            textAlign: 'center',
                            borderRadius: 3,
                            cursor: achievement.unlocked ? 'pointer' : 'default',
                            background: achievement.unlocked ? 
                              achievement.tier === 'diamond' ? 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)' :
                              achievement.tier === 'platinum' ? 'linear-gradient(135deg, #e0e0e0 0%, #b8b8b8 100%)' :
                              achievement.tier === 'gold' ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' :
                              achievement.tier === 'silver' ? 'linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%)' :
                              'linear-gradient(135deg, #CD7F32 0%, #A0522D 100%)' : 
                              'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                            border: `2px solid ${achievement.unlocked ? 
                              achievement.tier === 'diamond' ? '#00bcd4' :
                              achievement.tier === 'platinum' ? '#e0e0e0' :
                              achievement.tier === 'gold' ? '#FFD700' :
                              achievement.tier === 'silver' ? '#C0C0C0' : '#CD7F32' : 
                              '#e0e0e0'}`,
                            opacity: achievement.unlocked ? 1 : 0.5,
                            transition: 'all 0.3s ease',
                            '&:hover': achievement.unlocked ? {
                              transform: 'translateY(-4px) rotate(2deg)',
                              boxShadow: theme.shadows[8]
                            } : {}
                          }}
                          onClick={achievement.unlocked ? achievement.action : undefined}
                        >
                          <Typography variant="h4" sx={{ mb: 0.5, filter: achievement.unlocked ? 'none' : 'grayscale(100%)' }}>
                            {achievement.icon}
                          </Typography>
                          <Typography variant="caption" fontWeight="bold" display="block" noWrap>
                            {achievement.name}
                          </Typography>
                          {achievement.unlocked && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5 }}>
                              <CheckCircle 
                                sx={{ 
                                  fontSize: 12, 
                                  color: 'success.main',
                                }} 
                              />
                              <Typography variant="caption" sx={{ ml: 0.5, textTransform: 'uppercase', fontSize: '0.6rem' }}>
                                {achievement.tier}
                              </Typography>
                            </Box>
                          )}
                          {!achievement.unlocked && achievement.progress > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={achievement.progress}
                                sx={{ height: 4, borderRadius: 2 }}
                              />
                              <Typography variant="caption" sx={{ fontSize: '0.5rem' }}>
                                {achievement.progress.toFixed(0)}%
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </HolographicCard>
          </motion.div>

          {/* AI Insights Panel */}
          {aiInsights.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <HolographicCard sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <Psychology sx={{ mr: 1 }} />
                    AI Insights
                    <Chip 
                      label="LIVE" 
                      size="small" 
                      color="success"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <List dense>
                    {aiInsights.slice(0, 3).map((insight) => (
                      <ListItem
                        key={insight.id}
                        button
                        onClick={insight.action}
                        sx={{
                          mb: 1,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white'
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'transparent', fontSize: '1.5rem' }}>
                            {insight.icon}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={insight.title}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                {insight.message}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={insight.confidence}
                                  sx={{ flex: 1, mr: 1, height: 4 }}
                                />
                                <Typography variant="caption">
                                  {insight.confidence}% confidence
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Button 
                    fullWidth 
                    variant="outlined"
                    onClick={() => navigate('/scenarios?tab=insights')}
                    endIcon={<ChevronRight />}
                    sx={{ mt: 1 }}
                  >
                    View All AI Insights
                  </Button>
                </CardContent>
              </HolographicCard>
            </motion.div>
          )}

          {/* Performance Trends */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <HolographicCard sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <AutoGraph sx={{ mr: 1 }} />
                  Performance Trends
                  <Button 
                    size="small" 
                    sx={{ ml: 'auto' }}
                    onClick={() => navigate('/scenarios?tab=scenarios')}
                  >
                    Details
                  </Button>
                </Typography>
                {performanceTrends.length === 0 ? (
                  <Typography color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                    No trend data available
                  </Typography>
                ) : (
                  <Box>
                    {performanceTrends.slice(-6).map((trend, index) => (
                      <Box 
                        key={trend.month} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            px: 1
                          }
                        }}
                        onClick={() => {
                          const monthScenarios = savedScenarios.filter(s => 
                            new Date(s.createdAt).toLocaleDateString('en-US', { month: 'short' }) === trend.month
                          );
                          if (monthScenarios.length > 0) {
                            handleViewScenario(monthScenarios[0]._id);
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 'medium' }}>
                          {trend.month}
                        </Typography>
                        <Box sx={{ flex: 1, ml: 2 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min(100, (trend.impact / 2000000) * 100)}
                            color={
                              trend.trend === 'up' ? 'success' : 
                              trend.trend === 'stable' ? 'warning' : 'error'
                            }
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {currencySymbol}{(trend.impact/1000).toFixed(0)}K
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {trend.count} scen
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </HolographicCard>
          </motion.div>

          {/* Quantum Support Access */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
<HolographicCard>
  <CardContent>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
      <SmartToy sx={{ mr: 1 }} />
      Quantum Support Center
      <Chip 
        label="LIVE" 
        size="small" 
        color="success"
        sx={{ ml: 1 }}
      />
    </Typography>
    
    {/* Support Stats */}
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-around', 
      mb: 3,
      p: 2,
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
      borderRadius: 2
    }}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" color="primary" fontWeight="bold">
          {supportStats.openTickets}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Open Tickets
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" color="success.main" fontWeight="bold">
          {supportStats.resolvedTickets}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Resolved
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" color="info.main" fontWeight="bold">
          {supportStats.responseTime}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Avg Response
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" color="warning.main" fontWeight="bold">
          {supportStats.satisfactionRate}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Satisfaction
        </Typography>
      </Box>
    </Box>
    
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {[
        {
          title: 'Technical Support',
          description: 'Get help with quantum features',
          icon: <SupportAgent />,
          action: () => setSupportDialogOpen(true),
          quantum: true,
          endpoint: '/api/support/tickets'
        },
        {
          title: 'Contact Sales',
          description: 'Enterprise solutions inquiry',
          icon: <Business />,
          action: () => setContactDialogOpen(true),
          endpoint: '/api/contact/sales'
        },
        {
          title: 'Feature Request',
          description: 'Suggest new quantum features',
          icon: <Lightbulb />,
          action: () => handleFeatureRequest(),
          quantum: true,
          endpoint: '/api/feature-requests'
        },
        {
          title: 'Bug Report',
          description: 'Report quantum system issues',
          icon: <BugReport />,
          action: () => handleBugReport(),
          quantum: true,
          endpoint: '/api/bug-reports'
        },
        {
          title: 'My Tickets',
          description: 'View support history',
          icon: <History />,
          action: () => handleViewMyTickets(),
          endpoint: '/api/support/tickets/user'
        },
        {
          title: 'Documentation',
          description: 'Quantum guides & API docs',
          icon: <Description />,
          action: () => handleOpenDocumentation(),
          endpoint: '/api/documentation'
        }
      ].map((option, index) => (
        <Grid item xs={6} key={option.title}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={option.icon}
            onClick={option.action}
            disabled={loadingSupport}
            sx={{
              height: '70px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              textAlign: 'left',
              p: 1,
              borderRadius: 2,
              borderColor: option.quantum ? 'primary.main' : 'divider',
              position: 'relative',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3
              }
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              {option.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {option.description}
            </Typography>
            {option.quantum && (
              <Chip 
                label="QUANTUM" 
                size="small" 
                color="primary" 
                sx={{ 
                  position: 'absolute', 
                  top: 4, 
                  right: 4,
                  height: 16, 
                  fontSize: '0.5rem' 
                }} 
              />
            )}
          </Button>
        </Grid>
      ))}
    </Grid>

    {/* Premium Support Banner */}
    <Box sx={{ 
      mt: 2, 
      p: 2, 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
      borderRadius: 3, 
      color: 'white',
      textAlign: 'center'
    }}>
      <Typography variant="body2" fontWeight="bold" gutterBottom>
        🚀 Premium Quantum Support
      </Typography>
      <Typography variant="caption" display="block" sx={{ mb: 1, opacity: 0.9 }}>
        Dedicated experts & priority AI assistance
      </Typography>
      <Button 
        size="small" 
        startIcon={<WorkspacePremium />}
        onClick={() => handleUpgradeSupport()}
        sx={{ 
          background: 'rgba(255,255,255,0.2)',
          color: 'white',
          '&:hover': { background: 'rgba(255,255,255,0.3)' }
        }}
      >
        Upgrade Now
      </Button>
    </Box>
  </CardContent>
</HolographicCard>

          </motion.div>
        </Grid>
      </Grid>

      {/* Enhanced Dialogs */}
      <QuantumExportDialog />
      <QuantumQuickAnalysisDialog />

      {/* Scenario Context Menu */}
      <Menu
        anchorEl={scenarioMenuAnchor}
        open={Boolean(scenarioMenuAnchor)}
        onClose={() => setScenarioMenuAnchor(null)}
      >
        <MenuItem onClick={() => selectedScenario && handleViewScenario(selectedScenario._id)}>
          <ListItemIcon><PlayArrow fontSize="small" /></ListItemIcon>
          <ListItemText>Run Quantum Analysis</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedScenario && handleDuplicateScenario(selectedScenario._id)}>
          <ListItemIcon><FileDownload fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicate Scenario</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedScenario && navigate('/scenarios?tab=scenarios')}>
          <ListItemIcon><CompareArrows fontSize="small" /></ListItemIcon>
          <ListItemText>Compare with Others</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => selectedScenario && handleDeleteScenario(selectedScenario._id)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><Warning fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete Scenario</ListItemText>
        </MenuItem>
      </Menu>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleUserAction('profile')}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          <ListItemText>Quantum Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleUserAction('settings')}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText>Quantum Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleUserAction('billing')}>
          <ListItemIcon><CreditScore fontSize="small" /></ListItemIcon>
          <ListItemText>Billing & Quantum Credits</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleUserAction('support')}>
          <ListItemIcon><SupportAgent fontSize="small" /></ListItemIcon>
          <ListItemText>Quantum Support</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleUserAction('contact')}>
          <ListItemIcon><ContactSupport fontSize="small" /></ListItemIcon>
          <ListItemText>Contact Quantum Team</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleUserAction('logout')}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      
	  {/* Demo Dialog */}
      <Dialog
        open={demoDialogOpen}
        onClose={() => setDemoDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PlayArrow sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                ROIAnalyzer Interactive Demo
              </Typography>
              <Chip 
                label="GUIDED TOUR" 
                color="primary" 
                size="small" 
                sx={{ ml: 2 }}
              />
            </Box>
            <IconButton 
              onClick={() => setDemoDialogOpen(false)}
              sx={{
                background: 'rgba(0,0,0,0.1)',
                '&:hover': {
                  background: 'rgba(0,0,0,0.2)'
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <ROIAnalyzerDemo />
        </DialogContent>
        <DialogActions sx={{ p: 2, background: 'rgba(255,255,255,0.8)' }}>
          <Button 
            onClick={() => setDemoDialogOpen(false)}
            variant="outlined"
          >
            Close Demo
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setDemoDialogOpen(false);
              navigate('/scenarios?tab=analyze');
            }}
            startIcon={<RocketLaunch />}
          >
            Start Real Analysis
          </Button>
        </DialogActions>
      </Dialog>
{/* Notification Center */}
      <Dialog
        open={notificationCenterOpen}
        onClose={() => setNotificationCenterOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Notifications sx={{ mr: 1 }} />
            Quantum Notifications
            <Chip 
              label={notifications.filter(n => !n.read).length} 
              size="small" 
              color="primary"
              sx={{ ml: 1 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {notifications.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No quantum notifications
            </Typography>
          ) : (
            <List>
              {notifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    bgcolor: notification.read ? 'transparent' : 'action.hover',
                    borderRadius: 1,
                    mb: 1,
                    border: notification.quantum ? '1px solid' : 'none',
                    borderColor: notification.quantum ? 'primary.light' : 'transparent'
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ 
                      bgcolor: 
                        notification.type === 'success' ? 'success.main' :
                        notification.type === 'warning' ? 'warning.main' : 'info.main'
                    }}>
                      {notification.type === 'success' ? <CheckCircle /> :
                       notification.type === 'warning' ? <Warning /> : <Info />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {notification.title}
                        {notification.quantum && (
                          <Chip label="QUANTUM" size="small" color="primary" sx={{ height: 20 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2">{notification.message}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notification.time}
                        </Typography>
                      </Box>
                    }
                  />
                  {!notification.read && (
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      bgcolor: 'primary.main', 
                      borderRadius: '50%' 
                    }} />
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationCenterOpen(false)}>
            Close
          </Button>
          <Button 
            onClick={() => setNotifications(prev => prev.map(n => ({ ...n, read: true })))}
          >
            Mark All Read
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Us Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactSupport sx={{ mr: 1 }} />
              Contact Quantum Team
            </Box>
            <IconButton onClick={() => setContactDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleContactSubmit}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Your Name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={contactForm.category}
                    label="Category"
                    onChange={(e) => setContactForm({...contactForm, category: e.target.value})}
                  >
                    <MenuItem value="general">General Inquiry</MenuItem>
                    <MenuItem value="sales">Quantum Sales</MenuItem>
                    <MenuItem value="partnership">Partnership</MenuItem>
                    <MenuItem value="feature-request">Quantum Feature Request</MenuItem>
                    <MenuItem value="technical">Technical Question</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={contactForm.priority}
                    label="Priority"
                    onChange={(e) => setContactForm({...contactForm, priority: e.target.value})}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  required
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleContactSubmit}
            startIcon={<Send />}
          >
            Send Quantum Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Support Dialog */}
      <Dialog
        open={supportDialogOpen}
        onClose={() => setSupportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SupportAgent sx={{ mr: 1 }} />
              Quantum Technical Support
            </Box>
            <IconButton onClick={() => setSupportDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSupportSubmit}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Issue Type</InputLabel>
                  <Select
                    value={supportForm.issueType}
                    label="Issue Type"
                    onChange={(e) => setSupportForm({...supportForm, issueType: e.target.value})}
                  >
                    <MenuItem value="technical">Technical Issue</MenuItem>
                    <MenuItem value="quantum">Quantum Feature</MenuItem>
                    <MenuItem value="billing">Billing Problem</MenuItem>
                    <MenuItem value="feature">Feature Not Working</MenuItem>
                    <MenuItem value="bug">Bug Report</MenuItem>
                    <MenuItem value="performance">Performance Issue</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Urgency</InputLabel>
                  <Select
                    value={supportForm.urgency}
                    label="Urgency"
                    onChange={(e) => setSupportForm({...supportForm, urgency: e.target.value})}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={supportForm.subject}
                  onChange={(e) => setSupportForm({...supportForm, subject: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={6}
                  value={supportForm.description}
                  onChange={(e) => setSupportForm({...supportForm, description: e.target.value})}
                  placeholder="Please describe your quantum issue in detail..."
                  required
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSupportSubmit}
            startIcon={<Send />}
          >
            Submit Quantum Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quantum Speed Dial */}
      <SpeedDial
        ariaLabel="Quantum Speed Dial"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        icon={<SpeedDialIcon />}
      >
        <SpeedDialAction
          icon={<RocketLaunch />}
          tooltipTitle="Quick Analysis"
          onClick={() => navigate('/scenarios?tab=analyze')}
        />
        <SpeedDialAction
          icon={<Insights />}
          tooltipTitle="AI Insights"
          onClick={() => navigate('/scenarios?tab=insights')}
        />
        <SpeedDialAction
          icon={<Add />}
          tooltipTitle="New Scenario"
          onClick={() => navigate('/scenarios?tab=analyze')}
        />
        <SpeedDialAction
          icon={<SupportAgent />}
          tooltipTitle="Support"
          onClick={() => setSupportDialogOpen(true)}
        />
      </SpeedDial>
    </Container>
  );
};

// Helper function for ROI calculation
const calculateROI = (scenario) => {
  const investment = (scenario.turnover || 100000000) * 0.05;
  return investment > 0 ? (scenario.totalImpact || 0) / investment : 1;
};

export default Dashboard;